import { useI18N } from "@sundae/react-hooks";
import { Table, Text, toolkitCx } from "@sundae/ui-toolkit";
import { ColumnDef, TableOptions, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import cx from "classnames";
import { useMemo } from "react";
import { joyrideSelectors } from "../../../../constants/joyride.constants";
import { Program } from "../../../../gql/generated/yieldFarmingV2.sdk";
import { useYieldFarmingProgramsQuery } from "../../../../gql/hooks/farms.query";
import { useAdaData } from "../../../../hooks/useAdaData";
import { slotToUnix } from "../../../../utils/slots.utils";
import ProgramDailyRewards from "../ProgramDailyRewards";

const RewardsLeaderBoardTable = () => {
  const { t } = useI18N("farms");
  const { data: adaData } = useAdaData();
  const { data, isLoading } = useYieldFarmingProgramsQuery();

  const programs = useMemo(() => {
    return data?.reduce((acc: Program[], program) => {
      const lastDailyRewards = program.lastDailyRewards
        ? slotToUnix(program.lastDailyRewards.slot) * 1000
        : undefined;

      const now = new Date().getTime();

      if (!lastDailyRewards || lastDailyRewards >= now) {
        acc.push(program);
      }

      return acc;
    }, []);
  }, [data]);

  const yesterdayDate = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);

    yesterday.setDate(today.getDate() - 1);

    const yyyy = yesterday.getFullYear();
    const mm = String(yesterday.getMonth() + 1).padStart(2, "0");
    const dd = String(yesterday.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const columns = useMemo<ColumnDef<Program>[]>(
    () => [
      {
        header: t("landingPage.rewardsLeaderBoard.tableHeader.program"),
        id: "program",
        cell: ({ row: { original } }) => {
          return (
            <Text tag="div" size="sm">
              {original.label}
            </Text>
          );
        },
      },
      {
        header: t("landingPage.rewardsLeaderBoard.tableHeader.last30d"),
        id: "allTime",
        cell: ({
          row: {
            original: { emittedAsset, id },
          },
        }) => {
          const rollupKey = `program:${id}`;

          return <ProgramDailyRewards rollupKey={rollupKey} emittedAssetId={emittedAsset} />;
        },
      },
      {
        header: t("landingPage.rewardsLeaderBoard.tableHeader.last24h"),
        id: "last24h",
        cell: ({
          row: {
            original: { emittedAsset, id },
          },
        }) => {
          const rollupKey = `file:${yesterdayDate}/${id}`;

          return <ProgramDailyRewards rollupKey={rollupKey} emittedAssetId={emittedAsset} />;
        },
      },
    ],
    [adaData, yesterdayDate]
  );

  const tableOptions: TableOptions<Program> = useMemo(
    () => ({
      data: programs,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [columns, programs]
  );

  return (
    <div
      className="flex flex-col gap-6"
      data-guide-selector={joyrideSelectors.guides.yieldFarming.leaderboard}
    >
      <Text tag="div" weight="semibold">
        {t("landingPage.rewardsLeaderBoard.title")}
      </Text>
      <Table
        tableCellClassName="!px-2"
        bodyClassName="text-sm"
        tableOptions={tableOptions}
        headersClassName={cx("text-xs", toolkitCx.layout.background.card)}
        isLoading={isLoading}
        isSortable
        textAlignment="left"
        withStickyTableHeader
      />
    </div>
  );
};

export default RewardsLeaderBoardTable;
